import { computed, signal, inject, OnInit, Component, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog, MatDialogTitle, MatDialogContent, MatDialogActions } from '@angular/material/dialog';
import { MatTooltip } from '@angular/material/tooltip';
import { LearningResourceEnrollment } from 'src/app/models/learning_resource_enrollment.model';
import { CompanyProduct } from 'src/app/models/company_product.model';
import { CompanyProductPhase } from 'src/app/models/company_product_phase.model';
import { ProductTaskBoard } from 'src/app/models/product_task_board.model';
import { ProductTaskBoardTimeBudget } from 'src/app/models/product_task_board_time_budget.model';
import { WorkingTime } from 'src/app/models/working_time.model';
import { WorkingTimeCategory } from 'src/app/models/working_time_category.model';
import { EmployeeCalendarRegisterWithRelationsSerialized } from 'src/app/models/employee_calendar_register.model';
import { HourStringToSecondsPipe } from 'src/app/pipes/hour-string-to-seconds.pipe';
import { SecondsToHourStringPipe } from 'src/app/pipes/seconds-to-hour-string.pipe';
import { ApiService } from 'src/app/services/api.service';
import { PermissionsService } from 'src/app/services/permissions.service';
import { PlatformService } from 'src/app/services/platform.service';
import { UserService } from 'src/app/services/user.service';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatLabel, MatFormField, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatDatepickerInput, MatDatepickerToggle, MatDatepicker } from '@angular/material/datepicker';
import { MatSelect } from '@angular/material/select';
import { MatOption, MatAutocompleteTrigger, MatAutocomplete } from '@angular/material/autocomplete';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { RouterLink } from '@angular/router';
import { firstValueFrom, lastValueFrom } from 'rxjs';
import dayjs from 'dayjs';
import { LocationSelectorComponent } from 'src/app/components/main/shared/location-selector/location-selector.component';
import {
  LinkLocationIpDialogComponent,
  LinkLocationIpDialogData,
  LinkLocationIpDialogResult,
} from 'src/app/components/main/shared/location-selector/link-location-ip-dialog/link-location-ip-dialog.component';
import { LocationRelatableTypeEnum } from 'src/app/enums/location-relatable-type.enum';
import { LocationSelectorValue } from 'src/app/models/location.model';
import { ICloudPrivateRelayWarningBannerComponent } from 'src/app/components/main/shared/icloud-private-relay-warning-banner/icloud-private-relay-warning-banner.component';

type ReviewTargetBudgetOption = ProductTaskBoardTimeBudget & {
  review_target_label: string;
};

@Component({
  selector: 'app-add-new-working-time-dialog',
  templateUrl: './add-new-working-time-dialog.component.html',
  styleUrls: ['./add-new-working-time-dialog.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MatDialogTitle,
    CdkScrollable,
    MatDialogContent,
    FormsModule,
    ReactiveFormsModule,
    MatLabel,
    MatFormField,
    MatInput,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatSuffix,
    MatDatepicker,
    MatSelect,
    MatOption,
    MatAutocompleteTrigger,
    MatAutocomplete,
    MatIconButton,
    MatIcon,
    MatDialogActions,
    MatButton,
    RouterLink,
    LocationSelectorComponent,
    ICloudPrivateRelayWarningBannerComponent,
    MatTooltip,
  ],
})
export class AddNewWorkingTimeDialogComponent implements OnInit {
  readonly learning_resource_relatable_type = 'App\\Models\\LearningResource';
  readonly LocationRelatableTypeEnum = LocationRelatableTypeEnum;

  dialogRef = inject<MatDialogRef<AddNewWorkingTimeDialogComponent>>(MatDialogRef);
  private dialog = inject(MatDialog);

  data = inject<{
    product_task_board: ProductTaskBoard | undefined;
    product_task_board_time_budget: ProductTaskBoardTimeBudget;
    workingTime: WorkingTime | undefined;
    is_editing: boolean | undefined;
    review_target_budget_options?: ProductTaskBoardTimeBudget[];
    review_task_board_time_budget?: ProductTaskBoardTimeBudget;
  }>(MAT_DIALOG_DATA);

  private readonly reviewTargetSlugs = ['task-time-tracking', 'product-design'] as const;

  private user = inject(UserService);
  protected fb = inject(UntypedFormBuilder);
  private api = inject(ApiService);
  private permissions = inject(PermissionsService);
  protected platform = inject(PlatformService);

  readonly show_icloud_private_relay_warning = signal(false);

  form: UntypedFormGroup;
  dateControl: UntypedFormControl = new UntypedFormControl(new Date(), Validators.required);
  timeControl: UntypedFormControl = new UntypedFormControl(
    '',
    Validators.compose([Validators.required, Validators.pattern(/^(?:(?<hours>\d+)[h]\s*)?(?:(?<minutes>\d+)[m]\s*)?$/)])
  );
  startTimeControl: UntypedFormControl = new UntypedFormControl(
    '',
    Validators.compose([Validators.required, Validators.pattern(/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/)])
  );
  companyProductControl: UntypedFormControl = new UntypedFormControl('');
  companyProductPhaseControl: UntypedFormControl = new UntypedFormControl('');

  working_time_categories: WorkingTimeCategory[] = [];
  all_working_time_categories: WorkingTimeCategory[] = [];
  company_product_categories: WorkingTimeCategory[] = [];
  product_task_board: ProductTaskBoard | undefined;

  filtered_company_products: CompanyProduct[] = [];
  filtered_company_product_phases: CompanyProductPhase[] = [];

  selected_working_time_categories: any[] = [];
  available_working_time_categories: WorkingTimeCategory[][] = [];
  is_company_product_category: boolean = false;
  is_company_product_phase_category: boolean = false;
  is_review_task_registration: boolean = false;
  is_learning_resource_category: boolean = false;

  active_learning_resource_enrollments: LearningResourceEnrollment[] = [];

  readonly reviewTargetBudgets = signal<ReviewTargetBudgetOption[]>([]);
  readonly selectedWorkingTimeCategorySlug = signal<string | null>(null);
  readonly is_task_registration = this.data.product_task_board !== undefined;

  readonly showReviewTargetSelector = computed((): boolean => {
    return this.reviewTargetBudgets().length > 0 && this.selectedWorkingTimeCategorySlug() === 'review-task';
  });

  readonly has_trip_on_selected_date = signal(false);
  readonly has_gps_coordinates = signal(false);
  readonly location_permission_denied = signal(false);
  readonly location_loading = signal(true);

  readonly is_location_required_and_missing = computed((): boolean => {
    return this.has_trip_on_selected_date() && !this.has_gps_coordinates() && !this.location_loading();
  });

  readonly save_button_tooltip = computed((): string => {
    if (!this.is_location_required_and_missing()) {
      return '';
    }

    if (this.location_permission_denied()) {
      return this.platform.isElectronApp()
        ? 'Tienes un viaje ese día. Activa la ubicación para Bisual Intranet en los ajustes del sistema para poder registrar tiempo.'
        : 'Tienes un viaje ese día. Activa los permisos de ubicación del navegador para poder registrar tiempo.';
    }

    return 'Tienes un viaje ese día. Necesitamos tu ubicación GPS para registrar tiempo.';
  });

  maxDate: Date = new Date();

  @ViewChild(LocationSelectorComponent) location_selector?: LocationSelectorComponent;
  @ViewChild('companyProductPhaseAutocompleteTrigger') company_product_phase_autocomplete_trigger?: MatAutocompleteTrigger;

  constructor() {
    const data = this.data;

    // if data has task, it means that we are adding a working time to a task
    if (data.product_task_board != null && data.product_task_board_time_budget != null) {
      this.product_task_board = data.product_task_board;
      this.is_review_task_registration =
        this.isReviewTaskBudget(data.product_task_board_time_budget) || data.workingTime?.working_time_category?.slug === 'review-task';
      const review_target_budgets = data.review_target_budget_options ?? [];
      this.reviewTargetBudgets.set(this.mapReviewTargetBudgetOptions(review_target_budgets));
      if (this.is_review_task_registration) {
        this.selectedWorkingTimeCategorySlug.set('review-task');
      } else if (data.workingTime?.working_time_category?.slug != null) {
        this.selectedWorkingTimeCategorySlug.set(data.workingTime.working_time_category.slug);
      }
      const initial_relatable_id = this.is_review_task_registration
        ? this.resolveInitialReviewTargetRelatableId(data.workingTime, review_target_budgets)
        : (data.workingTime?.relatable_id ?? data.product_task_board_time_budget.id);

      let duration_in_seconds_real = data.workingTime ? data.workingTime.duration_in_seconds_real : 0;
      const initial_review_task_budget_id = this.is_review_task_registration ? this.resolveReviewTaskBoardTimeBudgetId() : null;

      this.form = this.fb.group({
        user_id: [data.workingTime?.user_id ?? '', Validators.required],
        working_time_category_id: [data.workingTime?.working_time_category_id ?? '', Validators.required],
        started_at: [new Date(), Validators.compose([Validators.required])],
        duration_in_seconds_real: [duration_in_seconds_real, Validators.compose([Validators.required, Validators.min(1)])],
        description: [
          data.workingTime ? data.workingTime.description : '',
          Validators.compose([Validators.required, Validators.minLength(4)]),
        ],
        relatable_id: [initial_relatable_id, Validators.required],
        relatable_type: ['App\\Models\\ProductTaskBoardTimeBudget', Validators.required],
        review_task_product_task_board_time_budget_id: [
          initial_review_task_budget_id,
          this.is_review_task_registration ? Validators.required : null,
        ],
        company_product_id: [''],
        product_task_board_id: [data.product_task_board.id],
        company_product_phase_id: [''],
        location_id: [null as number | null],
        location_ip_id: [null as number | null],
        lat: [null as number | null],
        lon: [null as number | null],
        attach_current_ip: [false],
        can_attach_current_ip: [false],
        location_name: [null as string | null],
        valid: [true, Validators.requiredTrue],
      });

      if (data.is_editing && data.workingTime) {
        const started_at = new Date(data.workingTime.started_at);
        this.dateControl.setValue(started_at);
        this.form.patchValue({ started_at, user_id: data.workingTime.user_id });
        this.timeControl.setValue(new SecondsToHourStringPipe().transform(duration_in_seconds_real));
        this.startTimeControl.setValue(('00' + started_at.getHours()).slice(-2) + ':' + ('00' + started_at.getMinutes()).slice(-2));
      } else if (duration_in_seconds_real != 0) {
        this.timeControl.setValue(new SecondsToHourStringPipe().transform(duration_in_seconds_real));
        this.startTimeControl.setValue(
          ('00' + new Date(new Date().setSeconds(new Date().getSeconds() - duration_in_seconds_real)).getHours()).slice(-2) +
            ':' +
            ('00' + new Date(new Date().setSeconds(new Date().getSeconds() - duration_in_seconds_real)).getMinutes()).slice(-2)
        );
      }
    } else {
      this.form = this.fb.group({
        user_id: ['', Validators.required],
        working_time_category_id: ['', Validators.required],
        started_at: [new Date(), Validators.compose([Validators.required])],
        duration_in_seconds_real: [0, Validators.compose([Validators.required, Validators.min(1)])],
        description: ['', Validators.compose([Validators.required, Validators.minLength(4)])],
        company_product_id: [''],
        company_product_phase_id: [''],
        relatable_id: [''],
        relatable_type: [''],
        location_id: [null as number | null],
        location_ip_id: [null as number | null],
        lat: [null as number | null],
        lon: [null as number | null],
        attach_current_ip: [false],
        can_attach_current_ip: [false],
        location_name: [null as string | null],
        valid: [false, Validators.requiredTrue],
      });
    }
  }

  ngOnInit(): void {
    this.initUser();
    this.fetchWorkingTimeCategories();
    this.listenDateTimeControl();
    this.listenCompanyProductControl();
    this.listenCategoryControl();
    this.checkFormValid();
    this.listenStartTimeControl();
    this.listenDateControl();
    void this.checkTripOnSelectedDate();
    if (this.timeControl.value == '') this.fetchSuggestedTime();
  }

  onIcloudPrivateRelayWarningChange(show: boolean): void {
    this.show_icloud_private_relay_warning.set(show);
  }

  selectCompanyProduct(p: CompanyProduct): void {
    if (!this.canRegisterWorkingTimeInCompanyProduct(p.id)) {
      this.resetCompanyProduct();

      return;
    }

    this.form.patchValue({ company_product_id: p.id, relatable_id: p.id, relatable_type: 'App\\Models\\CompanyProduct' });

    this.fetchWorkingTimeCategoryChildren(
      this.selected_working_time_categories[this.selected_working_time_categories.length - 1].id.toString()
    );

    this.fetchCompanyProductCategories(
      this.selected_working_time_categories[this.selected_working_time_categories.length - 1].id.toString()
    );
  }

  selectCompanyProductPhase(p: CompanyProductPhase) {
    this.form.patchValue({ company_product_phase_id: p.id, relatable_id: p.id, relatable_type: 'App\\Models\\CompanyProductPhase' });
  }

  async autoSelectCompanyProductPhase(): Promise<void> {
    if (this.filtered_company_product_phases.length === 0) {
      await this.loadFilteredCompanyProductPhases('');
    }

    if (this.filtered_company_product_phases.length === 1) {
      const single_phase = this.filtered_company_product_phases[0];
      this.companyProductPhaseControl.setValue(single_phase);
      this.selectCompanyProductPhase(single_phase);
    }
  }

  onCompanyProductPhaseInputFocus(): void {
    const search = typeof this.companyProductPhaseControl.value === 'string' ? this.companyProductPhaseControl.value : '';

    this.loadFilteredCompanyProductPhases(search).then((): void => {
      setTimeout((): void => {
        this.company_product_phase_autocomplete_trigger?.openPanel();
      });
    });
  }

  resetCompanyProduct() {
    this.resetCategoryRelatableFields();
    this.available_working_time_categories = [];
    this.selected_working_time_categories = [this.selected_working_time_categories[0]];
    this.is_company_product_phase_category = false;
  }

  resetCompanyProductPhase() {
    this.form.patchValue({
      company_product_phase_id: '',
      relatable_type: 'App\\Models\\CompanyProduct',
      relatable_id: this.form.value['company_product_id'],
    });
    this.companyProductPhaseControl.setValue('');
  }

  displayFnCompanyProduct(p: CompanyProduct): string {
    return p ? p.title : '';
  }

  displayFnCompanyProductPhase(phase: CompanyProductPhase): string {
    return phase.company_product_phase_type.name;
  }

  async save(): Promise<void> {
    if (this.is_location_required_and_missing()) {
      return;
    }

    if (this.is_company_product_category && !this.canRegisterWorkingTimeInCompanyProduct(this.form.value['company_product_id'])) {
      return;
    }

    if (this.product_task_board != null) {
      const category = this.getWorkingTimeCategoryById(String(this.form.value['working_time_category_id']));

      if (category != null) {
        this.syncReviewTaskFormFields(category, false);
      }
    } else if (this.selected_working_time_categories.length > 0) {
      this.form.patchValue({
        working_time_category_id: this.selected_working_time_categories[this.selected_working_time_categories.length - 1].id,
      });
    }

    await this.resolveAttachCurrentIpBeforeClose();

    const { can_attach_current_ip: _can_attach, location_name: _location_name, ...payload } = this.form.value;
    this.dialogRef.close(payload);
  }

  retryLocation(): void {
    this.location_selector?.retryLocation();
  }

  can_submit(): boolean {
    return this.form.valid && this.checkCategoryAssignability() && !this.location_loading() && !this.is_location_required_and_missing();
  }

  onCategoryChange(event: any, level: number) {
    if (event != null && event.value != null) {
      // if level number is less than this.available_working_time_categories length, it means that we are selecting a parent and we have to remove the children in
      // this.available_working_time_categories and the selected categories in this.selected_working_time_categories
      if (level < this.available_working_time_categories.length) {
        this.available_working_time_categories.splice(level);
      }
      if (level < this.selected_working_time_categories.length) {
        this.selected_working_time_categories.splice(level);
      }

      const category = this.getWorkingTimeCategoryById(event.value.toString());
      if (category != null) {
        const is_root_category = category.parent_category_slug == null;

        if (is_root_category) {
          this.is_company_product_category = category.slug == 'company-product';
        }

        this.is_company_product_phase_category = category.needs_relation_when_registering_time == 'company_product_phase';

        this.is_learning_resource_category =
          category.slug === 'realizar-curso' || category.needs_relation_when_registering_time === 'learning_resource';

        // Avoid keeping a previous product/course relatable when switching category path
        const should_reset_category_relatable_fields =
          this.product_task_board == null &&
          (is_root_category ||
            (!this.is_company_product_category && !this.is_company_product_phase_category && !this.is_learning_resource_category));

        if (should_reset_category_relatable_fields) {
          this.resetCategoryRelatableFields();
        }

        if (this.is_company_product_phase_category) {
          this.autoSelectCompanyProductPhase();
        } else if (this.is_company_product_category && !is_root_category) {
          this.form.patchValue({ relatable_type: 'App\\Models\\CompanyProduct', relatable_id: this.form.value['company_product_id'] });
        }

        this.selected_working_time_categories.push(category);
        this.selectedWorkingTimeCategorySlug.set(category.slug);

        if (this.product_task_board != null) {
          this.syncReviewTaskFormFields(category, true);
        }

        if (this.is_learning_resource_category) {
          this.loadActiveLearningResourceEnrollments();
        } else {
          this.active_learning_resource_enrollments = [];
        }

        if (category.slug != 'company-product') this.fetchWorkingTimeCategoryChildren(event.value);
      }
    }
  }

  checkCategoryAssignability() {
    if (this.selectedWorkingTimeCategorySlug() === 'review-task') {
      if (this.resolveReviewTaskBoardTimeBudgetId() == null || this.reviewTargetBudgets().length === 0) {
        return false;
      }

      if (this.form.value['relatable_id'] == null || this.form.value['review_task_product_task_board_time_budget_id'] == null) {
        return false;
      }
    }

    if (this.product_task_board != null) {
      const category_id = this.form.value['working_time_category_id'];

      return category_id != null && category_id !== '';
    }

    if (
      this.selected_working_time_categories.length == 0 ||
      this.available_working_time_categories.length > this.selected_working_time_categories.length
    ) {
      return false;
    }

    const category_id = this.selected_working_time_categories[this.selected_working_time_categories.length - 1].id;
    if (category_id == null) {
      return false;
    }

    const category = this.getWorkingTimeCategoryById(category_id.toString());

    return category ? category.is_working_time_registrable : false;
  }

  private isReviewTaskBudget(budget: ProductTaskBoardTimeBudget): boolean {
    return (
      budget.budgetable_type === 'App\\Models\\WorkingTimeCategory' &&
      budget.budgetable != null &&
      (budget.budgetable as WorkingTimeCategory).slug === 'review-task'
    );
  }

  private resolveReviewTaskBoardTimeBudgetId(): number | null {
    if (this.data.workingTime?.review_task_product_task_board_time_budget_id != null) {
      return this.data.workingTime.review_task_product_task_board_time_budget_id;
    }

    if (this.data.review_task_board_time_budget?.id != null) {
      return this.data.review_task_board_time_budget.id;
    }

    if (this.isReviewTaskBudget(this.data.product_task_board_time_budget)) {
      return this.data.product_task_board_time_budget.id;
    }

    return null;
  }

  private resolveInitialReviewTargetRelatableId(
    workingTime: WorkingTime | undefined,
    reviewTargetBudgets: ProductTaskBoardTimeBudget[]
  ): number | null {
    if (workingTime?.relatable_id != null) {
      const existingTarget = reviewTargetBudgets.find(budget => budget.id === workingTime.relatable_id);

      if (existingTarget != null) {
        return existingTarget.id;
      }
    }

    const defaultSlug = this.getDefaultReviewTargetSlug();

    if (defaultSlug != null) {
      const matchingBudget = reviewTargetBudgets.find((ptbtb: ProductTaskBoardTimeBudget): boolean => {
        return this.getReviewTargetSlugFromBudget(ptbtb) === defaultSlug;
      });

      if (matchingBudget != null) {
        return matchingBudget.id;
      }
    }

    return reviewTargetBudgets[0]?.id ?? null;
  }

  private getDefaultReviewTargetSlug(): (typeof this.reviewTargetSlugs)[number] | null {
    const contextSlug = this.getReviewTargetSlugFromBudget(this.data.product_task_board_time_budget);

    if (contextSlug != null) {
      return contextSlug;
    }

    const board = this.data.product_task_board;
    const activeBudgetId = board?.active_product_task_board_time_budget_id;

    if (activeBudgetId == null || board?.product_task_board_time_budgets == null) {
      return null;
    }

    const activeBudget = board.product_task_board_time_budgets.find((ptbtb: ProductTaskBoardTimeBudget): boolean => {
      return ptbtb.id === activeBudgetId;
    });

    return this.getReviewTargetSlugFromBudget(activeBudget);
  }

  private getReviewTargetSlugFromBudget(budget: ProductTaskBoardTimeBudget | undefined): (typeof this.reviewTargetSlugs)[number] | null {
    if (budget?.budgetable_type !== 'App\\Models\\WorkingTimeCategory' || budget.budgetable == null) {
      return null;
    }

    const slug = (budget.budgetable as WorkingTimeCategory).slug;

    if (slug === 'task-time-tracking' || slug === 'development') {
      return 'task-time-tracking';
    }

    if (slug === 'product-design') {
      return 'product-design';
    }

    return null;
  }

  private fetchSuggestedTime(date: Date | null = null) {
    const user = this.user.getCurrentUser();
    if (user.role.slug === 'worker') {
      this.api.getTimeWithoutRegister(this.form.value['user_id'], date).subscribe(data => {
        if (data != null) {
          this.timeControl.patchValue(new SecondsToHourStringPipe().transform(data));
        }
      });
    }
  }

  private initUser() {
    this.user.currentUser.subscribe(data => {
      if (data != null && this.form.value['user_id'] == '') {
        this.form.patchValue({ user_id: data.id });
        void this.checkTripOnSelectedDate();
      }
    });
  }

  private fetchWorkingTimeCategories(company_product_id: string = '') {
    const params: any = {};
    if (company_product_id != '') params.company_product_id = company_product_id;
    if (this.product_task_board != null) {
      // Use product_task_board_id (the actual task ID), not the product_task_id
      const params = { product_task_board_id: this.data.product_task_board_time_budget.product_task_board_id };

      this.api.getTaskWorkingTimeCategories(params).subscribe(data => {
        this.working_time_categories = data;

        if (this.data.is_editing && this.data.workingTime) {
          const working_time_category_id = this.data.workingTime.working_time_category_id;
          const category = this.getWorkingTimeCategoryById(String(working_time_category_id));
          const patch: Record<string, unknown> = {
            working_time_category_id,
            valid: true,
          };

          if (this.is_review_task_registration) {
            const review_task_id = this.resolveReviewTaskBoardTimeBudgetId();

            if (review_task_id != null) {
              patch['review_task_product_task_board_time_budget_id'] = review_task_id;
            }
          }

          this.form.patchValue(patch);
          this.selectedWorkingTimeCategorySlug.set(category?.slug ?? null);

          return;
        }

        if (this.data.product_task_board_time_budget != null) {
          let working_time_category_id: number | null = this.data.product_task_board_time_budget.budgetable_id;
          if (this.data.product_task_board_time_budget.budgetable_type === 'App\\Models\\CompanyProductTeam') {
            working_time_category_id = this.working_time_categories.find(wtc => wtc.slug === 'task-time-tracking')!.id;
          }

          this.form.patchValue({ working_time_category_id: working_time_category_id });
          this.onCategoryChange({ value: working_time_category_id }, -1);
        }
      });
    } else {
      this.api.getUserRegistrableWorkingTimeCategories(this.form.value['user_id'], params).subscribe(data => {
        this.working_time_categories = data.filter(c => c.parent_category_slug == null);
      });
    }
    this.api.getAllWorkingTimeCategories(params).subscribe(data => {
      this.all_working_time_categories = data;
    });
  }

  private fetchCompanyProductCategories(id: string) {
    this.api.getWorkingTimeCategoryChildren(id, { company_product_id: this.form.value['company_product_id'] }).subscribe(data => {
      this.available_working_time_categories[this.available_working_time_categories.length - 1] = data;
    });
  }

  private fetchWorkingTimeCategoryChildren(id: string) {
    // every time a category is selected, we will get the children
    this.api
      .getWorkingTimeCategoryChildren(id, {
        company_product_id: this.form.value['company_product_id'],
        include_working_time_registrable_children: 1,
      })
      .subscribe(data => {
        const working_time_subcategories = this.filterWorkingTimeSubcategories(data);
        if (working_time_subcategories.length !== 0) {
          this.available_working_time_categories.push(working_time_subcategories);
        }
      });
  }

  private filterWorkingTimeSubcategories(working_time_categories: WorkingTimeCategory[]): WorkingTimeCategory[] {
    return working_time_categories.filter(
      (working_time_category: WorkingTimeCategory): boolean => working_time_category.slug !== 'evaluation-of-formation'
    );
  }

  private listenDateTimeControl() {
    this.timeControl.valueChanges.subscribe((time: string) => {
      let duration_in_seconds_real = 0;

      if (this.timeControl.valid) {
        duration_in_seconds_real = this.getHoursMinutesTime(time);
      }

      const new_date = new Date(
        new Date(this.dateControl.value).setSeconds(new Date(this.dateControl.value).getSeconds() - duration_in_seconds_real)
      );

      this.form.patchValue({
        duration_in_seconds_real: duration_in_seconds_real,
        // started_at: date - aixo ja es fa automaticament al fer set value de StartTime
      });
      this.startTimeControl.setValue(
        duration_in_seconds_real == 0 ? '' : ('00' + new_date.getHours()).slice(-2) + ':' + ('00' + new_date.getMinutes()).slice(-2)
      );
    });

    this.dateControl.valueChanges.subscribe((data: Date) => {
      if (this.dateControl.valid && this.timeControl.valid) {
        const res_hours_minutes = this.startTimeControl.value.split(':');
        const hours = res_hours_minutes[0];
        const minutes = res_hours_minutes[1];
        const new_date: Date = new Date(data);
        new_date.setHours(hours);
        new_date.setMinutes(minutes);

        this.form.patchValue({
          started_at: new_date,
        });
      }
    });
  }

  private getWorkingTimeCategoryById(id: string) {
    let cat: WorkingTimeCategory = null as any;

    for (const c of this.working_time_categories) {
      if (c.id.toString() == id) {
        cat = c;
        break;
      }
    }

    if (cat == null) {
      for (const c of this.company_product_categories) {
        if (c.id.toString() == id) {
          cat = c;
          break;
        }
      }
    }

    if (cat == null) {
      for (const c of this.all_working_time_categories) {
        if (c.id.toString() == id) {
          cat = c;
          break;
        }
      }
    }

    return cat;
  }

  private listenCompanyProductControl() {
    this.companyProductControl.valueChanges.subscribe(data => {
      this.api.getCompanyProducts({ search: data }).subscribe(data => {
        this.filtered_company_products = data.data.filter(company_product =>
          this.canRegisterWorkingTimeInCompanyProduct(company_product.id)
        );
      });
    });

    this.companyProductPhaseControl.valueChanges.subscribe(data => {
      if (typeof data !== 'string' && data != null) {
        return;
      }

      this.loadFilteredCompanyProductPhases(data ?? '');
    });
  }

  private loadFilteredCompanyProductPhases(search: string): Promise<void> {
    return lastValueFrom(
      this.api.getCompanyProductPhases({ search, company_product_id: this.form.value['company_product_id'], is_active: true })
    ).then(data => {
      this.filtered_company_product_phases = data.data;
    });
  }

  private canRegisterWorkingTimeInCompanyProduct(company_product_id: number | string | null | undefined): boolean {
    if (company_product_id == null || company_product_id === '') {
      return false;
    }

    if (this.permissions.isAdmin()) {
      return true;
    }

    if (this.permissions.isClient()) {
      return true;
    }

    return this.permissions.isBisualProductionTeamInCompanyProduct(company_product_id.toString());
  }

  private listenCategoryControl() {
    this.form.controls['working_time_category_id'].valueChanges.subscribe(data => {
      this.syncSelectedWorkingTimeCategorySlug(data);

      if (this.product_task_board != null && data != null && data !== '') {
        const category = this.getWorkingTimeCategoryById(String(data));

        if (category != null) {
          this.syncReviewTaskFormFields(category, true);
        }
      }

      if (data != null && data != '') {
        if (!this.is_company_product_category) this.form.patchValue({ company_product_id: '' });
      } else this.form.patchValue({ company_product_id: '' });
    });
  }

  private isReviewTargetRelatableId(relatable_id: number | null | undefined): boolean {
    if (relatable_id == null) {
      return false;
    }

    return this.reviewTargetBudgets().some((budget: ReviewTargetBudgetOption): boolean => budget.id === relatable_id);
  }

  private syncReviewTaskFormFields(category: WorkingTimeCategory, reset_relatable_if_invalid: boolean): void {
    if (category.slug === 'review-task') {
      const patch: Record<string, unknown> = {
        review_task_product_task_board_time_budget_id: this.resolveReviewTaskBoardTimeBudgetId(),
        relatable_type: 'App\\Models\\ProductTaskBoardTimeBudget',
      };

      const current_relatable_id = this.form.value['relatable_id'];

      if (reset_relatable_if_invalid && !this.isReviewTargetRelatableId(current_relatable_id)) {
        patch['relatable_id'] = this.resolveInitialReviewTargetRelatableId(this.data.workingTime, this.reviewTargetBudgets());
      }

      this.form.patchValue(patch);

      return;
    }

    this.form.patchValue({
      review_task_product_task_board_time_budget_id: null,
      relatable_id: this.data.product_task_board_time_budget.id,
      relatable_type: 'App\\Models\\ProductTaskBoardTimeBudget',
    });
  }

  private syncSelectedWorkingTimeCategorySlug(category_id: unknown): void {
    if (category_id == null || category_id === '') {
      this.selectedWorkingTimeCategorySlug.set(null);

      return;
    }

    const category = this.getWorkingTimeCategoryById(String(category_id));
    this.selectedWorkingTimeCategorySlug.set(category?.slug ?? null);
  }

  private mapReviewTargetBudgetOptions(budgets: ProductTaskBoardTimeBudget[]): ReviewTargetBudgetOption[] {
    return budgets.map(
      (budget: ProductTaskBoardTimeBudget): ReviewTargetBudgetOption => ({
        ...budget,
        review_target_label: this.buildReviewTargetLabel(budget),
      })
    );
  }

  private buildReviewTargetLabel(budget: ProductTaskBoardTimeBudget): string {
    const slug = (budget.budgetable as WorkingTimeCategory)?.slug;

    if (slug === 'product-design') {
      return 'Revisión de diseño (UX/UI)';
    }

    if (slug === 'task-time-tracking') {
      return 'Revisión de desarrollo (DEV)';
    }

    return (budget.budgetable as WorkingTimeCategory)?.name ?? 'Revisión';
  }

  private listenDateControl(): void {
    this.dateControl.valueChanges.subscribe((value: Date) => {
      this.fetchSuggestedTime(value);
      void this.checkTripOnSelectedDate();
    });
  }

  private checkFormValid() {
    this.form.valueChanges.subscribe(data => {
      let valid: boolean = true;

      if (this.is_company_product_phase_category && data.company_product_phase_id == '') valid = false;
      if (!this.is_company_product_phase_category && this.is_company_product_category && data.company_product_id == '') valid = false;
      if (this.is_company_product_category && !this.canRegisterWorkingTimeInCompanyProduct(data.company_product_id)) valid = false;
      if (
        this.is_learning_resource_category &&
        (this.active_learning_resource_enrollments.length === 0 || data.relatable_id == null || data.relatable_id === '')
      ) {
        valid = false;
      }

      this.form.patchValue({ valid: valid }, { emitEvent: false });
    });
  }

  private listenStartTimeControl() {
    this.startTimeControl.valueChanges.subscribe(data => {
      if (this.startTimeControl.valid) {
        const splitted: number[] = data.split(':');
        const current_date: Date = new Date(this.dateControl.value);
        current_date.setHours(splitted[0]);
        current_date.setMinutes(splitted[1]);

        this.form.patchValue({
          started_at: current_date,
        });
      } else {
        this.form.patchValue({
          started_at: '',
        });
      }
    });
  }

  private getHoursMinutesTime(time: string) {
    return new HourStringToSecondsPipe().transform(time);
  }

  onLearningResourceSelected(learning_resource_id: number): void {
    this.form.patchValue({
      relatable_id: learning_resource_id,
      relatable_type: this.learning_resource_relatable_type,
    });
  }

  onLocationChange(value: LocationSelectorValue): void {
    this.form.patchValue({
      location_ip_id: value.location_ip_id,
      location_id: value.location_id,
      lat: value.lat,
      lon: value.lon,
      attach_current_ip: false,
      can_attach_current_ip: value.can_attach_current_ip ?? false,
      location_name: value.location_name ?? null,
    });
    this.has_gps_coordinates.set(value.gps_available === true);
    this.location_permission_denied.set(value.location_permission_denied === true);
    this.location_loading.set(value.locating === true);
  }

  private async checkTripOnSelectedDate(): Promise<void> {
    const user_id = this.form.value['user_id'];
    const date = this.dateControl.value;

    if (user_id == null || user_id === '' || date == null) {
      this.has_trip_on_selected_date.set(false);
      return;
    }

    try {
      const day_start = dayjs(date).startOf('day').toDate();
      const day_end = dayjs(date).endOf('day').toDate();
      const ecrs = await firstValueFrom(this.api.getEmployeeCalendarRegistersForUserSerialized(user_id.toString(), day_start, day_end));
      const has_trip = ecrs.some((ecr: EmployeeCalendarRegisterWithRelationsSerialized) => this.isTrip(ecr));
      this.has_trip_on_selected_date.set(has_trip);
    } catch {
      this.has_trip_on_selected_date.set(false);
    }
  }

  private isTrip(ecr: EmployeeCalendarRegisterWithRelationsSerialized): boolean {
    return ecr.origin_location_id != null && ecr.destination_location_id != null;
  }

  private async resolveAttachCurrentIpBeforeClose(): Promise<void> {
    const can_attach_current_ip = this.form.value['can_attach_current_ip'] === true;
    const location_name = this.form.value['location_name'] as string | null;

    if (!can_attach_current_ip || location_name == null || location_name === '') {
      this.form.patchValue({ attach_current_ip: false });
      return;
    }

    const result = await firstValueFrom(
      this.dialog
        .open(LinkLocationIpDialogComponent, {
          width: '480px',
          data: { location_name } satisfies LinkLocationIpDialogData,
        })
        .afterClosed()
    );

    const dialog_result = result as LinkLocationIpDialogResult | undefined;
    this.form.patchValue({ attach_current_ip: dialog_result?.link_ip === true });
  }

  private loadActiveLearningResourceEnrollments(): void {
    const user_id = this.form.value['user_id'];
    if (!user_id) {
      return;
    }

    this.api.getActiveLearningResourceEnrollmentsForUser(user_id).subscribe({
      next: (learning_resource_enrollments: LearningResourceEnrollment[]): void => {
        this.active_learning_resource_enrollments = learning_resource_enrollments;

        const first_enrollment = learning_resource_enrollments[0];

        if (first_enrollment?.learning_resource_id != null) {
          this.form.patchValue({
            relatable_id: first_enrollment.learning_resource_id,
            relatable_type: this.learning_resource_relatable_type,
          });
        } else {
          this.form.patchValue({ relatable_id: '', relatable_type: '' });
        }
      },
    });
  }

  private resetCategoryRelatableFields(): void {
    this.form.patchValue({
      company_product_id: '',
      company_product_phase_id: '',
      relatable_id: '',
      relatable_type: '',
    });
    this.companyProductControl.setValue('');
    this.companyProductPhaseControl.setValue('');
    this.filtered_company_products = [];
    this.filtered_company_product_phases = [];
  }
}
